import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { getCategories } from "../../http/cars/types";
import { useSubsidiaryVatCodesApi } from "../../http/subsidiaryVatCodes/api";
import { SubsidiaryVatCode } from "../../http/subsidiaryVatCodes/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { isWithinLast48h } from "../../types/utils";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CreateSubsidiaryVatCodeButton from "./CreateSubsidiaryVatCodeButton";
import DeleteSubsidiaryVatCodeDialog from "./DeleteSubsidiaryVatCodeDialog";
import EditSubVatDialog from "./EditSubsidiaryVatCode";
import SubsidiaryVatCodeHistoryDialog from "./SubsidiaryVatCodeHistoryDialog";

const SUBSIDIARY_VAT_CODE_FIELDS = [
  "id",
  "createdAt",
  "subsidiary.id",
  "subsidiary.name",
  "subsidiary.countryIsoCodeAlpha2",
  "carCategory",
  "productCode",
  "vatCode",
  "versionNumber",
] as const;
type SubsidiaryVatCodeField = (typeof SUBSIDIARY_VAT_CODE_FIELDS)[number];
type SubsidiaryVatCodeSearch = NestedPick<SubsidiaryVatCode, SubsidiaryVatCodeField>;

const SubsidiaryVatCodes: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const subVatCodesApi = useSubsidiaryVatCodesApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<SubsidiaryVatCodeSearch>();
  const [itemToEdit, setItemToEdit] = useState<SubsidiaryVatCodeSearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<SubsidiaryVatCodeSearch>();
  const { id: subsidiaryId } = useParams<{ id: string }>();

  const query: PaginationQueryWithFields<SubsidiaryVatCodeSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: SUBSIDIARY_VAT_CODE_FIELDS,
  };

  const result = subVatCodesApi.useAdminSubVatCodes(query, aborter.current.signal, subsidiaryId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: subVatCodesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const categories = getCategories(i18n.t);

  const getRowActions = (item: SubsidiaryVatCodeSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.subsidiaryVatCodes.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.subsidiaryVatCodes.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const content = (
    <>
      <Table<SubsidiaryVatCodeSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.subsidiaryVatCodes.insert && (
            <CreateSubsidiaryVatCodeButton subsidiaryId={subsidiaryId} />
          ),
        }}
        head={
          <>
            {!subsidiaryId && (
              <>
                <Table.Th
                  title={i18n.t.subsidiaries.singular}
                  fieldName="subVatCode.subsidiary.name"
                  order={orderOptions}
                  filters={filterOptions}
                  filter={TextColumnFilter}
                />
                <Table.Th
                  title={i18n.t.common.countryIsoCode}
                  fieldName="subVatCode.subsidiary.isoCode"
                  order={orderOptions}
                  filters={filterOptions}
                  filter={TextColumnFilter}
                />
              </>
            )}
            <Table.Th
              title={i18n.t.common.carCategory}
              fieldName="subVatCode.car.category"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
              formatter={(value) => categories.find((x) => x.value == value)?.label}
            />
            <Table.Th
              title={i18n.t.common.productCode}
              fieldName="subVatCode.product.code"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.vatCode}
              fieldName="subVatCode.vat.code"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            {(userSettings.permissions.subsidiaryVatCodes.update ||
              userSettings.permissions.subsidiaryVatCodes.delete) && <th></th>}
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            {!subsidiaryId && (
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                <CustomLink to={`/admin/subsidiaries/${item.subsidiary.id}`}>{item.subsidiary.name}</CustomLink>
              </td>
            )}
            {!subsidiaryId && <td>{item.subsidiary.countryIsoCodeAlpha2}</td>}
            <td>
              {!!subsidiaryId && isWithinLast48h(item.createdAt) && <NewItemIcon />}
              {categories.find((x) => x.value == item.carCategory)?.label}
            </td>
            <td>{item.productCode}</td>
            <td>{item.vatCode}</td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && (
        <DeleteSubsidiaryVatCodeDialog
          subsidiaryVatCodeId={itemToDelete.id}
          subsidiaryId={itemToDelete.subsidiary.id}
          onClose={() => setItemToDelete(undefined)}
        />
      )}

      {!!itemToEdit && (
        <EditSubVatDialog
          subVatCodeId={itemToEdit.id}
          onClose={() => setItemToEdit(undefined)}
          subsidiaryId={subsidiaryId}
        />
      )}

      {!!itemToShowVersions && (
        <SubsidiaryVatCodeHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );

  return subsidiaryId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.subsidiaryVatCodes.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default SubsidiaryVatCodes;
